import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { User } from '../../../../core/classes/user';
import { MapGroup } from '../../../../shared/classes/map-group';
import { ReportGroup } from '../../../reports/classes/report-group';
import { AuthService } from '../../../../services/auth.service';
import { UserService } from '../../../../services/user.service';
import { LayerService } from '../../../../services/layer.service';
import { ReportsService } from '../../../../services/reports.service';
import { SNACKBAR_DURATION } from '../../../../shared/constants/snackbar.constants';

@Component({
  selector: 'tgrid-user-access-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatIcon,
    MatCheckboxModule,
    MatButtonModule,
    MatExpansionModule,
    MatDividerModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './user-access-management.component.html',
  styleUrl: './user-access-management.component.scss',
})
export class UserAccessManagementComponent implements OnInit {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private layerService = inject(LayerService);
  private reportsService = inject(ReportsService);
  private snackBar = inject(MatSnackBar);

  protected users = signal<User[]>([]);
  protected selectedUser = signal<User | null>(null);
  protected searchText = signal<string>('');

  protected layerGroups = signal<MapGroup[]>([]);
  protected reportGroups = signal<ReportGroup[]>([]);

  protected selectedLayerIds = signal<Set<number>>(new Set());
  protected selectedReportIds = signal<Set<number>>(new Set());

  protected loadingAccess = signal<boolean>(false);

  protected filteredUsers = computed(() => {
    const search = this.searchText().toLowerCase();
    if (!search) return this.users();
    return this.users().filter(user =>
      user.firstName?.toLowerCase().includes(search) ||
      user.lastName?.toLowerCase().includes(search) ||
      user.username?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.organization?.toLowerCase().includes(search)
    );
  });

  ngOnInit(): void {
    this.authService.getExistingUsers().subscribe({
      next: (users) => this.users.set(users),
      error: () => this.snackBar.open('Failed to load users', 'X', { duration: SNACKBAR_DURATION.MEDIUM }),
    });

    this.layerService.getLayerGroups().subscribe({
      next: (groups) => this.layerGroups.set(groups),
      error: () => this.snackBar.open('Failed to load map layers', 'X', { duration: SNACKBAR_DURATION.MEDIUM }),
    });

    this.reportsService.fetchReports().subscribe({
      next: (groups) => this.reportGroups.set(groups),
      error: () => this.snackBar.open('Failed to load reports', 'X', { duration: SNACKBAR_DURATION.MEDIUM }),
    });
  }

  protected selectUser(user: User): void {
    this.selectedUser.set(user);
    this.loadingAccess.set(true);

    forkJoin({
      layerIds: this.userService.getUserLayerAccess(user.id),
      reportIds: this.userService.getUserReportAccess(user.id),
    }).subscribe({
      next: ({ layerIds, reportIds }) => {
        this.selectedLayerIds.set(new Set(layerIds));
        this.selectedReportIds.set(new Set(reportIds));
        this.loadingAccess.set(false);
      },
      error: () => {
        this.snackBar.open('Failed to load user access', 'X', { duration: SNACKBAR_DURATION.MEDIUM });
        this.loadingAccess.set(false);
      },
    });
  }

  protected isLayerSelected(layerId: number): boolean {
    return this.selectedLayerIds().has(layerId);
  }

  protected toggleLayer(layerId: number, checked: boolean): void {
    const ids = new Set(this.selectedLayerIds());
    if (checked) {
      ids.add(layerId);
    } else {
      ids.delete(layerId);
    }
    this.selectedLayerIds.set(ids);
  }

  protected isReportSelected(reportId: number): boolean {
    return this.selectedReportIds().has(reportId);
  }

  protected toggleReport(reportId: number, checked: boolean): void {
    const ids = new Set(this.selectedReportIds());
    if (checked) {
      ids.add(reportId);
    } else {
      ids.delete(reportId);
    }
    this.selectedReportIds.set(ids);
  }

  protected saveLayerAccess(): void {
    const user = this.selectedUser();
    if (!user) return;

    this.userService.updateUserLayerAccess(user.id, Array.from(this.selectedLayerIds())).subscribe({
      next: () => this.snackBar.open('Map layer access updated', 'X', { duration: SNACKBAR_DURATION.MEDIUM }),
      error: () => this.snackBar.open('Failed to update map layer access', 'X', { duration: SNACKBAR_DURATION.MEDIUM }),
    });
  }

  protected saveReportAccess(): void {
    const user = this.selectedUser();
    if (!user) return;

    this.userService.updateUserReportAccess(user.id, Array.from(this.selectedReportIds())).subscribe({
      next: () => this.snackBar.open('Report access updated', 'X', { duration: SNACKBAR_DURATION.MEDIUM }),
      error: () => this.snackBar.open('Failed to update report access', 'X', { duration: SNACKBAR_DURATION.MEDIUM }),
    });
  }

  protected isAllLayersInGroupSelected(groupId: number): boolean {
    const group = this.layerGroups().find(g => g.id === groupId);
    if (!group || !group.layers.length) return false;
    return group.layers.every(l => this.selectedLayerIds().has(l.id));
  }

  protected isSomeLayersInGroupSelected(groupId: number): boolean {
    const group = this.layerGroups().find(g => g.id === groupId);
    if (!group || !group.layers.length) return false;
    const selected = group.layers.filter(l => this.selectedLayerIds().has(l.id));
    return selected.length > 0 && selected.length < group.layers.length;
  }

  protected toggleAllLayersInGroup(groupId: number, checked: boolean): void {
    const group = this.layerGroups().find(g => g.id === groupId);
    if (!group) return;
    const ids = new Set(this.selectedLayerIds());
    group.layers.forEach(l => {
      if (checked) {
        ids.add(l.id);
      } else {
        ids.delete(l.id);
      }
    });
    this.selectedLayerIds.set(ids);
  }

  protected isAllReportsInGroupSelected(groupId: number): boolean {
    const group = this.reportGroups().find(g => g.id === groupId);
    if (!group || !group.reports.length) return false;
    return group.reports.every(r => this.selectedReportIds().has(r.id));
  }

  protected isSomeReportsInGroupSelected(groupId: number): boolean {
    const group = this.reportGroups().find(g => g.id === groupId);
    if (!group || !group.reports.length) return false;
    const selected = group.reports.filter(r => this.selectedReportIds().has(r.id));
    return selected.length > 0 && selected.length < group.reports.length;
  }

  protected toggleAllReportsInGroup(groupId: number, checked: boolean): void {
    const group = this.reportGroups().find(g => g.id === groupId);
    if (!group) return;
    const ids = new Set(this.selectedReportIds());
    group.reports.forEach(r => {
      if (checked) {
        ids.add(r.id);
      } else {
        ids.delete(r.id);
      }
    });
    this.selectedReportIds.set(ids);
  }
}
